// src/pages/Video/VideoPage.tsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
    Box, Button, Container, Grid, Image, Loader, Title, Text, Group, Spoiler, AspectRatio,
} from '@mantine/core';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
    loadVideos, selectVideosStatus, selectVideosError, selectVideoBySlug, selectVideoById,
} from '@/features/videos/videosSlice';
import type { VideoRecord } from '@/types/video';
import { useBodyClass } from '@/hooks';
import classes from '@/pages/Video/VideoPages.module.scss';

type RouteParams = { slug?: string; id?: string };

export const VideoPage: React.FC = () => {
    useBodyClass(classes.videosPage);

    const { slug, id } = useParams<RouteParams>();
    const key = slug ?? id ?? '';                 // поддержим и старые ссылки
    const isId = /^\d+$/.test(key);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const status = useAppSelector(selectVideosStatus);
    const error  = useAppSelector(selectVideosError);

    const post = useAppSelector(
        isId ? selectVideoById(key) : selectVideoBySlug(key)
    ) as VideoRecord | undefined;

    useEffect(() => {
        if (status === 'idle') dispatch(loadVideos());
    }, [status, dispatch]);

    const title = `${post?.title ?? ''}${post?.subtitle ? ` ${post.subtitle}` : ''}`.trim();
    const watchUrl = useMemo(
        () => (post ? `https://www.1napsgear.to/ama.php?vId=${post.id}` : '#'),
        [post]
    );

    const [imgOk, setImgOk] = useState(true);
    const onImgError = useCallback(() => setImgOk(false), []);

    const actions = useMemo(
        () => (
            <Group mb="md" gap="sm">
                <Button variant="outline" onClick={() => navigate(-1)}>
                    Back
                </Button>
            </Group>
        ),
        [navigate]
    );

    const content = useMemo(() => {
        if (status === 'loading' && !post) {
            return (
                <Container py="lg">
                    <Loader />
                </Container>
            );
        }
        if (status === 'failed') {
            return (
                <Container py="lg">
                    {actions}
                    <Text c="red">Failed to load videos: {String(error ?? 'Unknown error')}</Text>
                </Container>
            );
        }
        if (!post) {
            return (
                <Container py="lg">
                    {actions}
                    <Title order={3} c="red">Episode not found</Title>
                </Container>
            );
        }

        const thumb = imgOk ? post.thumb : '';

        return (
            <Container>
                {actions}
                <Grid gutter="xl" align="stretch">
                    <Grid.Col span={{ base: 12, md: 6 }}>
                        <AspectRatio ratio={16 / 9} mah={560}>
                            <Image
                                src={thumb}
                                alt={title || 'Video preview'}
                                radius="md"
                                onError={onImgError}
                                decoding="async"
                                loading="lazy"
                                fit="cover"
                            />
                        </AspectRatio>
                        <Box ta="center" mt="lg">
                            <Button component="a" href={watchUrl} target="_blank" rel="noopener noreferrer" size="lg">
                                Watch on NapsGear ↗
                            </Button>
                        </Box>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                        <Title order={2} mb="sm">{title || 'Untitled episode'}</Title>
                        {post.description ? (
                            <Spoiler maxHeight={180} showLabel="Show more" hideLabel="Show less">
                                <Box className="description" dangerouslySetInnerHTML={{ __html: post.description }} />
                            </Spoiler>
                        ) : (
                            <Text c="dimmed" mt="sm">No description provided.</Text>
                        )}
                    </Grid.Col>
                </Grid>
            </Container>
        );
    }, [status, post, error, actions, imgOk, onImgError, title, watchUrl]);

    return <Box component="section" className={classes.amaSingleVideo}>{content}</Box>;
};
